import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import WaveSurfer from 'wavesurfer.js';

import type { LibraryItem } from '@/lib/format';
import { isAudioItem, streamUrl } from '@/lib/format';

export interface PlayerTrack {
  id: string;
  filename: string;
  relativePath: string;
  parentPath: string;
  durationMs?: number;
}

interface PlayerContextValue {
  track: PlayerTrack | null;
  queue: PlayerTrack[];
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  isReady: boolean;
  waveformRef: (node: HTMLDivElement | null) => void;
  playTrack: (item: LibraryItem, queue?: LibraryItem[]) => void;
  togglePlay: () => void;
  play: () => void;
  pause: () => void;
  next: () => void;
  prev: () => void;
  seekBy: (deltaSeconds: number) => void;
  setVolume: (v: number) => void;
  toggleMute: () => void;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

function toTrack(item: LibraryItem): PlayerTrack | null {
  if (!isAudioItem(item)) return null;
  const parentPath =
    'parentPath' in item && typeof item.parentPath === 'string'
      ? item.parentPath
      : item.relativePath.includes('/')
        ? item.relativePath.slice(0, item.relativePath.lastIndexOf('/'))
        : '';
  return {
    id: item.id,
    filename: item.filename,
    relativePath: item.relativePath,
    parentPath,
    durationMs: 'durationMs' in item ? item.durationMs : undefined,
  };
}

export function PlayerProvider({ children }: { children: ReactNode }) {
  const wsRef = useRef<WaveSurfer | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loadGen = useRef(0);
  const queueRef = useRef<PlayerTrack[]>([]);

  const [track, setTrack] = useState<PlayerTrack | null>(null);
  const [queue, setQueue] = useState<PlayerTrack[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [containerReady, setContainerReady] = useState(false);

  queueRef.current = queue;

  const waveformRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    setContainerReady(Boolean(node));
  }, []);

  // Create / recreate WaveSurfer when track or container changes — destroy prior instance first.
  useEffect(() => {
    if (!track || !containerReady || !containerRef.current) {
      return;
    }

    const prev = wsRef.current;
    wsRef.current = null;
    if (prev) {
      try {
        prev.destroy();
      } catch {
        /* already destroyed */
      }
    }

    const gen = ++loadGen.current;
    const el = containerRef.current;
    el.replaceChildren();
    setIsReady(false);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    const ws = WaveSurfer.create({
      container: el,
      height: 36,
      waveColor: '#3a4254',
      progressColor: '#2dd4bf',
      cursorColor: '#e8eaef',
      cursorWidth: 1,
      barWidth: 2,
      barGap: 1,
      barRadius: 0,
      normalize: true,
      url: streamUrl(track.id),
      fetchParams: { credentials: 'include' },
    });

    wsRef.current = ws;
    // Initial volume; subsequent changes handled by the volume/mute effect below.
    ws.setVolume(muted ? 0 : volume);

    const onReady = () => {
      if (gen !== loadGen.current) return;
      setIsReady(true);
      setDuration(ws.getDuration());
      void ws.play().catch(() => undefined);
    };
    const onPlay = () => {
      if (gen !== loadGen.current) return;
      setIsPlaying(true);
    };
    const onPause = () => {
      if (gen !== loadGen.current) return;
      setIsPlaying(false);
    };
    const onTime = () => {
      if (gen !== loadGen.current) return;
      setCurrentTime(ws.getCurrentTime());
    };
    const onFinish = () => {
      if (gen !== loadGen.current) return;
      setIsPlaying(false);
      // Stop at end — do not auto-advance or loop. Replay via click / play button.
    };

    ws.on('ready', onReady);
    ws.on('play', onPlay);
    ws.on('pause', onPause);
    ws.on('timeupdate', onTime);
    ws.on('finish', onFinish);
    ws.on('interaction', () => {
      setCurrentTime(ws.getCurrentTime());
    });

    return () => {
      if (wsRef.current === ws) {
        wsRef.current = null;
      }
      try {
        ws.destroy();
      } catch {
        /* already destroyed */
      }
      if (gen === loadGen.current) {
        setIsReady(false);
        setIsPlaying(false);
      }
    };
    // volume/muted applied via separate effect so volume changes don't reload the track
  }, [track?.id, containerReady]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    ws.setVolume(muted ? 0 : volume);
  }, [volume, muted]);

  const playTrack = useCallback((item: LibraryItem, list?: LibraryItem[]) => {
    const next = toTrack(item);
    if (!next) return;
    if (list) {
      setQueue(list.map(toTrack).filter((t): t is PlayerTrack => t != null));
    }
    setTrack((prev) => {
      if (prev?.id === next.id) {
        const ws = wsRef.current;
        if (ws) {
          if (ws.isPlaying()) {
            void ws.pause();
          } else {
            const dur = ws.getDuration();
            const cur = ws.getCurrentTime();
            if (Number.isFinite(dur) && dur > 0 && cur >= dur - 0.05) {
              ws.setTime(0);
            }
            void ws.play();
          }
        }
        return prev;
      }
      return next;
    });
  }, []);

  const togglePlay = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    if (ws.isPlaying()) {
      void ws.pause();
      return;
    }
    const dur = ws.getDuration();
    const cur = ws.getCurrentTime();
    if (Number.isFinite(dur) && dur > 0 && cur >= dur - 0.05) {
      ws.setTime(0);
    }
    void ws.play();
  }, []);

  const play = useCallback(() => {
    void wsRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    wsRef.current?.pause();
  }, []);

  const next = useCallback(() => {
    if (!track) return;
    const idx = queue.findIndex((t) => t.id === track.id);
    const n = idx >= 0 ? queue[idx + 1] : undefined;
    if (n) setTrack(n);
  }, [track, queue]);

  const prev = useCallback(() => {
    if (!track) return;
    const ws = wsRef.current;
    if (ws && ws.getCurrentTime() > 2) {
      ws.setTime(0);
      return;
    }
    const idx = queue.findIndex((t) => t.id === track.id);
    const p = idx > 0 ? queue[idx - 1] : undefined;
    if (p) setTrack(p);
    else if (ws) ws.setTime(0);
  }, [track, queue]);

  const seekBy = useCallback((deltaSeconds: number) => {
    const ws = wsRef.current;
    if (!ws) return;
    const nextTime = Math.max(0, Math.min(ws.getDuration(), ws.getCurrentTime() + deltaSeconds));
    ws.setTime(nextTime);
    setCurrentTime(nextTime);
  }, []);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    if (clamped > 0) setMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => !m);
  }, []);

  const value = useMemo<PlayerContextValue>(
    () => ({
      track,
      queue,
      isPlaying,
      currentTime,
      duration,
      volume,
      muted,
      isReady,
      waveformRef,
      playTrack,
      togglePlay,
      play,
      pause,
      next,
      prev,
      seekBy,
      setVolume,
      toggleMute,
    }),
    [
      track,
      queue,
      isPlaying,
      currentTime,
      duration,
      volume,
      muted,
      isReady,
      waveformRef,
      playTrack,
      togglePlay,
      play,
      pause,
      next,
      prev,
      seekBy,
      setVolume,
      toggleMute,
    ],
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within PlayerProvider');
  return ctx;
}
